import type { Participant } from '../../shared/protocol';
import { galleryLayout, pickRecordingType, shareLayout, type Rect } from '../lib/recording';
import type { Store } from '../lib/store';
import type { MeetingState } from './MeetingSession';

// Local recording of the meeting, like Zoom's "Record on this computer": the people in your room
// (or the shared screen with the cameras beside it) are drawn on a 1280×720 canvas, every voice and
// the shared sound are mixed, and MediaRecorder writes a video file in the browser. Nothing goes
// through the server.

const W = 1280;
const H = 720;
const FPS = 24;
const COLORS = ['#e8590c', '#0e72ed', '#12a150', '#ae3ec9', '#d6336c', '#1098ad', '#f08c00', '#5c7cfa'];

export interface Recording {
  blob: Blob;
  extension: 'webm' | 'mp4';
  durationMs: number;
}

export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    'captureStream' in HTMLCanvasElement.prototype &&
    !!pickRecordingType((t) => MediaRecorder.isTypeSupported(t))
  );
}

function colorFor(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLORS[h % COLORS.length];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export class MeetingRecorder {
  private readonly canvas = document.createElement('canvas');
  private readonly ctx: CanvasRenderingContext2D;
  private readonly audio = new AudioContext();
  private readonly mix = this.audio.createMediaStreamDestination();
  private readonly sources = new Map<string, MediaStreamAudioSourceNode>();
  private readonly videos = new Map<string, HTMLVideoElement>();
  private readonly recorder: MediaRecorder;
  private readonly chunks: Blob[] = [];
  private readonly type: { mimeType: string; extension: 'webm' | 'mp4' };
  private clock: Worker | null = null;
  private fallbackTimer: ReturnType<typeof setInterval> | null = null;
  private frame = 0;
  private startedAt = 0;
  private pausedAt = 0;
  private pausedMs = 0;
  private stopped: Promise<Recording> | null = null;

  constructor(private readonly store: Store<MeetingState>) {
    const type = pickRecordingType((t) => MediaRecorder.isTypeSupported(t));
    if (!type) throw new Error('Este navegador no puede grabar vídeo.');
    this.type = type;
    this.canvas.width = W;
    this.canvas.height = H;
    this.ctx = this.canvas.getContext('2d')!;
    // A silent constant keeps the audio track alive even before anyone speaks.
    const silence = this.audio.createConstantSource();
    silence.offset.value = 0;
    silence.connect(this.mix);
    silence.start();
    this.draw();
    const video = this.canvas.captureStream(FPS).getVideoTracks()[0];
    const stream = new MediaStream([video, ...this.mix.stream.getAudioTracks()]);
    this.recorder = new MediaRecorder(stream, { mimeType: type.mimeType, videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 128_000 });
    this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
  }

  get state(): RecordingState {
    return this.recorder.state === 'paused' ? 'paused' : this.recorder.state === 'recording' ? 'recording' : 'inactive';
  }

  /** Recorded time, without the pauses. */
  get elapsedMs(): number {
    if (!this.startedAt) return 0;
    const end = this.pausedAt || Date.now();
    return end - this.startedAt - this.pausedMs;
  }

  start(): void {
    void this.audio.resume();
    this.syncAudio();
    this.recorder.start(1000);
    this.startedAt = Date.now();
    try {
      this.clock = new Worker(new URL('./recorderClock.worker.ts', import.meta.url), { type: 'module' });
      this.clock.onmessage = () => this.tick();
      this.clock.postMessage({ fps: FPS });
    } catch {
      this.fallbackTimer = setInterval(() => this.tick(), 1000 / FPS);
    }
  }

  pause(): void {
    if (this.recorder.state !== 'recording') return;
    this.recorder.pause();
    this.pausedAt = Date.now();
  }

  resume(): void {
    if (this.recorder.state !== 'paused') return;
    this.recorder.resume();
    this.pausedMs += Date.now() - this.pausedAt;
    this.pausedAt = 0;
  }

  /** Finishes the file. Safe to call more than once. */
  stop(): Promise<Recording> {
    this.stopped ??= new Promise((resolve) => {
      const durationMs = this.elapsedMs;
      const finish = () => {
        this.cleanup();
        const blob = new Blob(this.chunks, { type: this.type.mimeType.split(';')[0] });
        resolve({ blob, extension: this.type.extension, durationMs });
      };
      if (this.recorder.state === 'inactive') return finish();
      this.recorder.onstop = finish;
      this.recorder.stop();
    });
    return this.stopped;
  }

  private cleanup(): void {
    this.clock?.postMessage('stop');
    this.clock?.terminate();
    this.clock = null;
    if (this.fallbackTimer) clearInterval(this.fallbackTimer);
    for (const v of this.videos.values()) v.srcObject = null;
    this.videos.clear();
    for (const s of this.sources.values()) s.disconnect();
    this.sources.clear();
    this.recorder.stream.getTracks().forEach((t) => t.stop());
    void this.audio.close();
  }

  private tick(): void {
    this.frame++;
    if (this.frame % FPS === 0) this.syncAudio();
    this.draw();
  }

  // ── audio: every voice in my room, plus shared sound ──

  private syncAudio(): void {
    const s = this.store.get();
    const tracks = new Map<string, MediaStreamTrack>();
    const add = (t: MediaStreamTrack | null | undefined) => t && t.readyState === 'live' && tracks.set(t.id, t);
    add(s.micTrack);
    s.screenStream?.getAudioTracks().forEach(add);
    for (const p of this.people(s)) {
      if (p.id === s.selfId) continue;
      for (const id of [p.cameraStreamId, p.screenStreamId]) if (id) s.remoteStreams[id]?.getAudioTracks().forEach(add);
    }
    for (const [id, node] of this.sources) {
      if (!tracks.has(id)) {
        node.disconnect();
        this.sources.delete(id);
      }
    }
    for (const [id, track] of tracks) {
      if (this.sources.has(id)) continue;
      const node = this.audio.createMediaStreamSource(new MediaStream([track]));
      node.connect(this.mix);
      this.sources.set(id, node);
    }
  }

  // ── video: the same view as the meeting ──

  private people(s: MeetingState): Participant[] {
    return Object.values(s.participants).sort(
      (a, b) => Number(b.id === s.activeSpeakerId) - Number(a.id === s.activeSpeakerId) || a.joinedAt - b.joinedAt,
    );
  }

  private videoFor(key: string, stream: MediaStream | null): HTMLVideoElement | null {
    if (!stream) return null;
    let v = this.videos.get(key);
    if (!v) {
      v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      this.videos.set(key, v);
    }
    if (v.srcObject !== stream) {
      v.srcObject = stream;
      void v.play().catch(() => undefined);
    }
    return v.videoWidth ? v : null;
  }

  private cameraOf(s: MeetingState, p: Participant): HTMLVideoElement | null {
    if (p.id === s.selfId) {
      if (!s.videoOn || !s.cameraTrack) return null;
      const stream = (this.selfCamera?.getVideoTracks()[0] === s.cameraTrack ? this.selfCamera : (this.selfCamera = new MediaStream([s.cameraTrack]))) ?? null;
      return this.videoFor('self', stream);
    }
    if (!p.videoOn || !p.cameraStreamId) return null;
    return this.videoFor(`cam:${p.id}`, s.remoteStreams[p.cameraStreamId] ?? null);
  }

  private selfCamera: MediaStream | null = null;

  private draw(): void {
    const s = this.store.get();
    const c = this.ctx;
    c.fillStyle = '#111214';
    c.fillRect(0, 0, W, H);
    const people = this.people(s);
    const sharer = people.find((p) => p.sharing && !p.sharePaused);
    const shareStream = sharer ? (sharer.id === s.selfId ? s.screenStream : sharer.screenStreamId ? (s.remoteStreams[sharer.screenStreamId] ?? null) : null) : null;
    const share = sharer && shareStream ? this.videoFor(`screen:${sharer.id}`, shareStream) : null;

    if (share) {
      const strip = people.slice(0, 4);
      const layout = shareLayout(W, H, strip.length);
      this.drawContain(share, layout.share);
      strip.forEach((p, i) => this.drawTile(s, p, layout.tiles[i]));
      return;
    }
    const shown = people.slice(0, 9);
    galleryLayout(shown.length, W, H).forEach((r, i) => this.drawTile(s, shown[i], r));
  }

  private drawContain(v: HTMLVideoElement, r: Rect): void {
    const scale = Math.min(r.w / v.videoWidth, r.h / v.videoHeight);
    const w = v.videoWidth * scale;
    const h = v.videoHeight * scale;
    this.ctx.drawImage(v, r.x + (r.w - w) / 2, r.y + (r.h - h) / 2, w, h);
  }

  private drawTile(s: MeetingState, p: Participant, r: Rect): void {
    const c = this.ctx;
    c.save();
    c.beginPath();
    c.roundRect(r.x, r.y, r.w, r.h, 10);
    c.clip();
    c.fillStyle = '#232428';
    c.fillRect(r.x, r.y, r.w, r.h);
    const v = this.cameraOf(s, p);
    if (v) {
      // Cover the tile, like the meeting view.
      const scale = Math.max(r.w / v.videoWidth, r.h / v.videoHeight);
      const w = v.videoWidth * scale;
      const h = v.videoHeight * scale;
      c.drawImage(v, r.x + (r.w - w) / 2, r.y + (r.h - h) / 2, w, h);
    } else {
      const radius = Math.min(r.w, r.h) * 0.18;
      c.fillStyle = colorFor(p.id);
      c.beginPath();
      c.arc(r.x + r.w / 2, r.y + r.h / 2, radius, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#fff';
      c.font = `600 ${Math.round(radius * 0.8)}px -apple-system, "Segoe UI", Roboto, sans-serif`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillText(initials(p.name), r.x + r.w / 2, r.y + r.h / 2);
    }
    // Name label, with a red mark when muted.
    const muted = p.id === s.selfId ? s.audioMuted : p.audioMuted;
    const size = Math.max(11, Math.round(r.h * 0.06));
    c.font = `600 ${size}px -apple-system, "Segoe UI", Roboto, sans-serif`;
    c.textAlign = 'left';
    c.textBaseline = 'middle';
    const label = p.name;
    const tw = c.measureText(label).width + (muted ? size + 6 : 0);
    const lh = size + 10;
    c.fillStyle = 'rgba(0,0,0,0.6)';
    c.beginPath();
    c.roundRect(r.x + 8, r.y + r.h - lh - 8, tw + 16, lh, 5);
    c.fill();
    let x = r.x + 16;
    if (muted) {
      c.fillStyle = '#ff6b6b';
      c.beginPath();
      c.arc(x + size / 2 - 2, r.y + r.h - 8 - lh / 2, size / 3, 0, Math.PI * 2);
      c.fill();
      x += size + 6;
    }
    c.fillStyle = '#fff';
    c.fillText(label, x, r.y + r.h - 8 - lh / 2);
    c.restore();
    if (s.speaking[p.id] && !muted) {
      c.strokeStyle = '#2fbf71';
      c.lineWidth = 3;
      c.beginPath();
      c.roundRect(r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3, 10);
      c.stroke();
    }
  }
}

export type RecordingState = 'recording' | 'paused' | 'inactive';
